import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import api from 'api/api';
import useVersionInfo from 'hooks/useVersionInfo';
import { isV4Release } from 'utilities/constants/versions';

import { fetchV4LciaMethodsParams, V4LciaMethod } from './types';

const fetchV4LciaMethods = async (params: fetchV4LciaMethodsParams) => {
  const { data } = await api.get(`/web/v4/uprs/${params.uprId}/methods`, { params });

  return data;
};

/**
 * Only v4 releases serve this endpoint, so the query stays idle on 3.x — callers no longer pass the
 * release in. `enabled` is for non-version reasons to hold the request back (e.g. guests with no
 * scores). See useV4Dataset.
 */
const useV4LciaMethods = (uprId: string, enabled = true) => {
  const { ver: version, system_model } = useVersionInfo();
  return useQuery<V4LciaMethod[], AxiosError>({
    queryKey: ['v4methods', uprId, version, system_model],
    queryFn: () => fetchV4LciaMethods({ uprId, version, system_model }),
    // Keyed by the upr whose methods these are, and the list cannot change within a session, so a successful
    // fetch is never refetched on mount (an errored one still retries). Cards resolve against the route's list.
    enabled: isV4Release(version) && enabled && !!uprId,
    retry: false,
    staleTime: Infinity,
  });
};

export default useV4LciaMethods;
